import React, { useState } from "react";
import { Control, DefaultValues, FieldValues, UseFormWatch, useForm } from "react-hook-form";
import { Form } from "../../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import { cn } from "../../../../../lib/utils";
import EmptyRow from "./EmptyRow";
import RowActions from "./RowActions";

// ── Public types ─────────────────────────────────────────────────────────────

/**
 * Context injected into every `editCell` renderer.
 * Use `control` to wire RHF field components (FormInput, FormSelect, …)
 * and `watch` for reactive logic (e.g. hiding a field when another field
 * equals "Generic").
 */
export interface EditCellContext<TForm extends FieldValues> {
  control: Control<TForm>;
  watch: UseFormWatch<TForm>;
}

/**
 * Definition for a single column.
 *
 * - `cell`     : renders the cell in **read** mode from the full record.
 * - `editCell` : renders the cell in **edit** mode via RHF context.
 *               Omit for columns that are always read-only.
 */
export interface ColumnDef<TRecord, TForm extends FieldValues = FieldValues> {
  id: string;
  header: string;
  headerClassName?: string;
  cellClassName?: string;
  cell: (record: TRecord) => React.ReactNode;
  editCell?: (ctx: EditCellContext<TForm>) => React.ReactNode;
}

/**
 * A single entry in a grouped header row.
 * Use `headerGroups` to render a spanning top row above the column headers
 * (e.g. to label input-field sections like old Semantic UI tables did).
 */
export interface HeaderGroup {
  label: string;
  colSpan: number;
  className?: string;
}

// ── Component props ──────────────────────────────────────────────────────────

interface EditableTableProps<TRecord extends object, TForm extends FieldValues> {
  records: TRecord[];
  columns: ColumnDef<TRecord, TForm>[];
  /** RHF default values — used to initialise and reset the form. */
  defaultFormValues: DefaultValues<TForm>;
  /** Convert a record → form values when the user clicks Edit. */
  toFormValues: (record: TRecord) => TForm;
  /** Convert submitted form values → record patch, passed to `onSave`. */
  fromFormValues: (data: TForm) => Partial<TRecord>;
  /**
   * Called after a successful form submission.
   * Receives the record `id` and the patch returned by `fromFormValues`.
   */
  onSave: (id: string, patch: Partial<TRecord>) => void;
  onEdit?: (id: string) => void;
  onCancel?: (id: string) => void;
  /**
   * When provided, a delete icon button appears in the Actions cell.
   * Receives the record key value.
   */
  onDelete?: (id: string) => void;
  /**
   * Derive the unique row key from a record. Defaults to `(r) => (r as any).id`.
   * Override when records use a different primary-key field (e.g. `_id`).
   */
  rowKey?: (record: TRecord) => string;
  /**
   * Set to `false` to hide the Actions column entirely (read-only table).
   * @default true
   */
  showActions?: boolean;
  /**
   * Optional spanning header rows rendered above the column-header row.
   * Useful for labelling groups of columns (mirrors the Semantic UI
   * "Input field" banner pattern).
   */
  headerGroups?: HeaderGroup[];
  /** Additional className applied to each `<TableRow>` based on record/state. */
  rowClassName?: (record: TRecord, editing: boolean) => string;
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────

function EditableTable<TRecord extends object, TForm extends FieldValues>({
  records,
  columns,
  defaultFormValues,
  toFormValues,
  fromFormValues,
  onSave,
  onEdit,
  onCancel,
  onDelete,
  rowKey = (r: any) => r.id,
  showActions = true,
  headerGroups,
  rowClassName,
  emptyLabel = "records",
  className,
  disabled = false
}: EditableTableProps<TRecord, TForm>) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const form = useForm<TForm>({ defaultValues: defaultFormValues });
  const { control, reset, handleSubmit, watch } = form;

  const getKey = (r: TRecord) => rowKey(r);

  const handleEdit = (r: TRecord) => {
    if (disabled) return;
    const id = getKey(r);
    setEditingId(id);
    reset(toFormValues(r) as DefaultValues<TForm>);
    onEdit?.(id);
  };

  const handleSave = (id: string) =>
    handleSubmit(data => {
      onSave(id, fromFormValues(data));
      setEditingId(null);
      reset();
    })();

  const handleCancel = (id: string) => {
    setEditingId(null);
    reset();
    onCancel?.(id);
  };

  if (records.length === 0) return <EmptyRow label={emptyLabel} />;

  const ctx: EditCellContext<TForm> = { control, watch };

  return (
    <Form {...form}>
      <div className={cn("tw-overflow-x-auto -tw-mx-3", className)}>
        <Table className="tw-text-xs">
          <TableHeader>
            {headerGroups && (
              <TableRow>
                {headerGroups.map((g, i) => (
                  <TableHead
                    key={i}
                    colSpan={g.colSpan}
                    className={cn("tw-text-xs tw-font-semibold", g.className)}
                  >
                    {g.label}
                  </TableHead>
                ))}
              </TableRow>
            )}
            <TableRow className="tw-bg-muted/40">
              {columns.map(col => (
                <TableHead key={col.id} className={cn("tw-text-xs", col.headerClassName)}>
                  {col.header}
                </TableHead>
              ))}
              {showActions && (
                <TableHead className="tw-text-xs tw-text-right tw-w-[90px]">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map(r => {
              const id = getKey(r);
              const editing = editingId === id;
              return (
                <TableRow key={id} className={rowClassName?.(r, editing)}>
                  {columns.map(col => (
                    <TableCell key={col.id} className={col.cellClassName}>
                      {editing && col.editCell ? col.editCell(ctx) : col.cell(r)}
                    </TableCell>
                  ))}
                  {showActions && (
                    <TableCell className="tw-text-right">
                      <RowActions
                        editing={editing}
                        onEdit={() => handleEdit(r)}
                        onSave={() => handleSave(id)}
                        onCancel={() => handleCancel(id)}
                        onDelete={onDelete ? () => onDelete(id) : undefined}
                        disabled={disabled}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Form>
  );
}

export default EditableTable;
